import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Paper,
  useTheme,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export interface BottomModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxHeight?: string | number;
  minHeight?: string | number;
  fullScreen?: boolean;
  showDragIndicator?: boolean;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  disableBackdropClick?: boolean;
  customHeader?: React.ReactNode;
  customFooter?: React.ReactNode;
  headerActions?: React.ReactNode;
  paperProps?: any;
  contentProps?: any;
  headerProps?: any;
  footerProps?: any;
  onOpen?: () => void;
  onCloseComplete?: () => void;
}

const BottomModal: React.FC<BottomModalProps> = ({
  open,
  onClose,
  title,
  children,
  maxHeight = '80vh',
  minHeight = '200px',
  fullScreen = false,
  showDragIndicator = true,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  disableBackdropClick = false,
  customHeader,
  customFooter,
  headerActions,
  paperProps = {},
  contentProps = {},
  headerProps = {},
  footerProps = {},
  onOpen,
  onCloseComplete,
}) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Handle drag to close functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!showDragIndicator) return;
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentTouchY = e.touches[0].clientY;
    setCurrentY(currentTouchY);
    
    // Prevent scrolling when dragging
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const dragDistance = currentY - dragStartY;
    const threshold = 100; // pixels to drag down to close
    
    if (dragDistance > threshold) {
      onClose();
    }
    
    setIsDragging(false);
    setDragStartY(0);
    setCurrentY(0);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      onOpen?.();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, closeOnEscape, onClose, onOpen]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (disableBackdropClick || !closeOnBackdropClick) return;
    
    // Only close if clicking the backdrop, not the drawer content
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
    onCloseComplete?.();
  };

  const renderHeader = () => {
    if (customHeader) {
      return customHeader;
    }

    if (!title && !showCloseButton && !headerActions) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          pb: 1,
          minHeight: 56,
          zIndex: 500,
        }}
        {...headerProps}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {title && (
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                flex: 1,
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {headerActions}
          {showCloseButton && (
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  const renderDragIndicator = () => {
    if (!showDragIndicator) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.divider,
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        />
      </Box>
    );
  };

  const renderFooter = () => {
    if (!customFooter) return null;

    return (
      <Box
        sx={{
          p: 2,
          pt: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
        {...footerProps}
      >
        {customFooter}
      </Box>
    );
  };

  const drawerContent = (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300,
      }}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transform: isDragging ? `translateY(${Math.max(0, currentY - dragStartY)}px)` : 'none',
      }}
    >
      <Grid 
        container 
        spacing={0.5} 
        columns={12}
        sx={{
          minWidth: '320px',
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        {/* left gutter: hidden on xs, 2 cols on sm, 3 cols on md+ */}
        <Grid
          size={{ sm: 2, md: 4 }}
          sx={{ 
            display: { xs: "none", sm: "block" }, 
            minWidth: { sm: 'auto' }
          }}
        />
        
        {/* main content: full width on xs, 8 cols on sm, 4 cols on md+ */}
        <Grid 
          size={{ xs: 12, sm: 8, md: 4 }} 
          sx={{ 
            minWidth: { xs: '280px', sm: 'auto' },
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <Paper
            ref={drawerRef}
            sx={{
              height: fullScreen ? '100vh' : 'auto',
              maxHeight: fullScreen ? '100vh' : maxHeight,
              minHeight: fullScreen ? '100vh' : minHeight,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: fullScreen ? 0 : 16,
              borderBottomRightRadius: fullScreen ? 0 : 16,
              overflow: 'hidden',
              boxShadow: theme.shadows[24],
              ...paperProps.sx,
            }}
            {...paperProps}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {renderDragIndicator()}
            
            {renderHeader()}
            
            {(title || customHeader) && <Divider />}
            
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
              {...contentProps}
            >
              {children}
            </Box>
            
            {renderFooter()}
          </Paper>
        </Grid>

        {/* right gutter: mirror of left */}
        <Grid
          size={{ sm: 2, md: 4 }}
          sx={{ 
            display: { xs: "none", sm: "block" }, 
            minWidth: { sm: 'auto' }
          }}
        />
      </Grid>
    </motion.div>
  );

  return (
    <AnimatePresence>
      {open && (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          variant="temporary"
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
            },
            zIndex: theme.zIndex.modal,
          }}
          ModalProps={{
            keepMounted: true,
            disableScrollLock: false,
          }}
          BackdropProps={{
            onClick: handleBackdropClick,
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </AnimatePresence>
  );
};

export default BottomModal;